import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material'

import Button from '../components/Button'
import Dialog from '../components/Dialog'
import GridHeader from '../components/GridHeader'
import GridIconButton from '../components/GridIconButton'
import SelectionBar from '../components/selection/SelectionBar'
import SortableTableHead from '../components/SortableTableHead'
import ButtonRunDownload from '../components/ButtonRunDownload'
import GridCircularProgress from '../components/GridCircularProgress'
import { config } from '../settings/configs'
import { colours, titlesIscrizioni as titles } from '../settings/settings'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useGetCorsiQuery,
  useGetImpostazioniContattiQuery,
  useGetIscrizioniCorsiQuery,
  useLazyGetIscrizioneCorsiByIdQuery,
  useDeleteIscrizioneCorsiMutation,
} from '../hooks/api-slice'
import { sortAuto } from '../hooks/sort-functions'
import { mailTo } from '../hooks/utils-functions'
import { setIscrizioneCorsi } from '../container/iscrizioniCorsi'

const IscrizioniCorsi = () => {
  const { admin, arbitro, idArbitro, codiceUtente } = useSelector(
    state => state.home.infoUtente,
  )
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const { data: corsi } = useGetCorsiQuery()
  const { data: iscrizioniCorsi, isLoading } = useGetIscrizioniCorsiQuery(
    { admin, arbitro, idArbitro, codiceUtente },
    { skip: !(admin || codiceUtente) },
  )

  const sortingColumn = useSelector(
    state => state.sorting.iscrizioniCorsi?.column,
  )
  const sortingAscending = useSelector(
    state => state.sorting.iscrizioniCorsi?.ascending,
  )

  const selectedStagione = useSelector(
    state => state.selection.selectedStagione,
  )
  const iscrizioniCorsiN = sortAuto(
    iscrizioniCorsi?.filter(
      i =>
        !selectedStagione ||
        corsi
          ?.filter(c => c.stagione === selectedStagione)
          ?.map(c => c._id)
          ?.includes(i.idCorso),
    ),
    sortingColumn,
    sortingAscending,
  )

  const [triggerGetIscrizioneCorsiById] = useLazyGetIscrizioneCorsiByIdQuery()
  const [deleteIscrizioneCorsiMutation] = useDeleteIscrizioneCorsiMutation()

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { setFeedback } = useFeedback()

  document.title = `${config?.name} - Corsi di formazione`

  let columns = [
    { label: 'Corso', sortable: true, sortingField: 'idCorso' },
    { label: 'Date', sortable: false },
    { label: 'Nome', sortable: false },
    { label: 'Cognome', sortable: false },
    { label: 'Tessera', sortable: false },
  ]

  if (admin)
    columns = [
      ...columns,
      { label: 'Società', sortable: true, sortingField: 'codiceSocieta' },
    ]

  columns = [...columns, { label: '', sortable: false }]

  const [openDelete, setOpenDelete] = useState()
  const [deletingIscrizioneCorsi, setDeletingIscrizioneCorsi] = useState()

  const handleClickOpenDelete = i => {
    setDeletingIscrizioneCorsi(i)
    setOpenDelete(true)
  }

  const handleCloseDelete = () => {
    setOpenDelete()
    setDeletingIscrizioneCorsi()
  }

  const editIscrizioneCorsi = async id => {
    const result = await triggerGetIscrizioneCorsiById(id)
    if (!result?.error) {
      dispatch(setIscrizioneCorsi(result?.data))
      navigate('/iscrizione-corsi')
    }
  }

  const deleteIscrizioneCorsi = async () => {
    const result = await deleteIscrizioneCorsiMutation(
      deletingIscrizioneCorsi?._id,
    )
    if (!result?.error) {
      handleCloseDelete()
      setFeedback('Iscrizione eliminata correttamente.', false)
    }
  }

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Corsi di formazione' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar yStagione />
        {!admin && corsi?.filter(c => !!c.iscrizioniAperte)?.length > 0 && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <center>
              <Button
                to='/iscrizione-corsi'
                onClick={() => dispatch(setIscrizioneCorsi())}
                children='Nuova iscrizione'
              />
            </center>
          </Grid>
        )}
        {admin && (
          <Grid size={{ xs: 12, md: 'grow' }}>
            <ButtonRunDownload
              buttonText='Scarica Excel'
              algorithm='17_GenerateExcelIscrizioniCorsi'
              type='XLSX'
              disabled={!selectedStagione || !iscrizioniCorsiN?.length}
            />
          </Grid>
        )}
      </Grid>
      {(isLoading && <GridCircularProgress />) ||
        (!iscrizioniCorsi && (
          <GridHeader text='Elenco delle iscrizioni non trovato.' />
        )) ||
        (iscrizioniCorsiN?.length === 0 && (
          <GridHeader text='Non è ancora stata effettuata nessuna iscrizione.' />
        )) || (
          <Grid container paddingY={2} sx={{ width: '100%' }}>
            {!admin && (
              <Grid size={12}>
                <center>
                  <p>
                    Per domande sulle iscrizioni ai corsi scrivere a{' '}
                    {mailTo(contatti?.MailSegreteria)}.
                  </p>
                </center>
              </Grid>
            )}
            <Grid container sx={{ width: '100%' }}>
              <TableContainer component={Paper}>
                <Table size='small'>
                  <SortableTableHead
                    table={iscrizioniCorsiN}
                    columns={columns}
                    tableName='iscrizioniCorsi'
                  />
                  <TableBody>
                    {iscrizioniCorsiN?.map((i, index) => {
                      const iCorso = corsi?.find(c => c.id === i.idCorso)

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: colours.white,
                            ':hover': {
                              backgroundColor: colours.greyVeryLight,
                            },
                          }}
                          onClick={() => editIscrizioneCorsi(i.id)}
                        >
                          <TableCell>{iCorso?.nome}</TableCell>
                          <TableCell>{iCorso?.date?.join(' + ')}</TableCell>
                          <TableCell>{i.nome}</TableCell>
                          <TableCell>{i.cognome}</TableCell>
                          <TableCell>{i.tesseraPGS}</TableCell>
                          {admin && (
                            <TableCell>{i.codiceSocieta || '-'}</TableCell>
                          )}
                          <TableCell>
                            <GridIconButton
                              type='delete'
                              onClick={e => {
                                e.stopPropagation()
                                handleClickOpenDelete(i)
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      <Dialog
        open={openDelete}
        handleClose={handleCloseDelete}
        title={titles.titleDeleteCorsi}
        textUndo='Annulla'
        textConfirm='Ok'
        triggerFunction={deleteIscrizioneCorsi}
        error
      />
    </Grid>
  )
}

export default IscrizioniCorsi
